import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  LineaAsientosContableCall,
  getAsientoContableExportarExcel,
  getImportesTotalesAsientoContable,
  getLineaAsientosContable,
} from "api/AsientoContable.service"
import { useCuentaContableEmpresaAll } from "api/CuentaContableEmpresa.hook"
import { useCuentaContableCuentaAll } from "api/CuentaContable_Cuenta.hook"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import { LineasAsientosContablesByFilterDtoIProps } from "api/DTOs/LineasAsientosContablesByFilterDto"
import { getFacturasByIdSimple } from "api/Factura.service"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import { handleDirectDownloadExcel } from "common/context/DownloadFileProvider"
import ContabilidadService from "common/services/ContabilidadService"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import ModalAsientoContable from "features/Contabilidad/components/ModalAsientoContable/ModalAsientoContable"
import { useFilterMenu } from "hooks/useFilterMenu"
import { addLocale } from "primereact/api"
import { DataTableRowClickEventParams } from "primereact/datatable"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { backendDateFormat } from "utils/formatDate"
import {
  DateRangeModalFechas,
  ModalFechas,
} from "../LibroDiario/components/Modales/ModalFechas"
import { FilterMenu } from "./FilterMenu"
import { LIBRO_MAYOR_COLUMNS } from "./constants/LibroMayorColumns"
import {
  TIPO_ASIENTO_LABELS,
  TipoAsientoLabelsProps,
} from "./constants/TipoAsientoLabels"
import { LOCALE_FORMATS_EN, LOCALE_FORMATS_ES } from "./constants/utils"

addLocale("es", LOCALE_FORMATS_ES)
addLocale("en", LOCALE_FORMATS_EN)

const LibroMayor = () => {
  const { t } = useTranslation()
  const params = useParams()
  const [showExportDatesModal, setShowExportDatesModal] = useState(false)
  const currentYear = new Date().getFullYear()
  const fechaLowerLimit = new Date(currentYear, 0, 1, 0, 0, 0, 0)
  const fechaUpperLimit = new Date(currentYear, 11, 31, 23, 59, 59, 999)
  const DEFAULT_DATE = {
    min: fechaLowerLimit,
    max: fechaUpperLimit,
  }
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const dtRef = useRef<DataTablePrimeReact<
    LineasAsientosContablesByFilterDtoIProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const [date, setDate] = useState<DateRangeModalFechas>(DEFAULT_DATE)
  const [keyword, setKeyword] = useState<string | undefined>()
  const [selectedAsientoTipo, setSelectedAsientoTipo] =
    useState<TipoAsientoLabelsProps>(TIPO_ASIENTO_LABELS(t)[0])
  const [amount, setAmount] = useState<{
    min: number | undefined
    max: number | undefined
  }>({
    min: undefined,
    max: undefined,
  })
  const [auxLibroMayorNumber, setAuxLibroMayorNumber] = useState()
  const [totals, setTotals] = useState({
    debeTotal: null,
    haberTotal: null,
    saldoAcumulado: null,
    saldoPeriodo: null,
  })
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: FacturaReadingSimpleDtoProps | null
  }>({
    show: false,
    document: null,
  })

  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      })
    }
  }
  const [showModalAsiento, setShowModalAsiento] = useState(false)
  const [asientoSelectedEdit, setAsientoSelectedEdit] = useState<string>()
  const fetchCuentaContableEmpresa = (queryParams: $TSMigrationFixMe) => {
    return ContabilidadService.getCuentaContableEmpresa(queryParams).then(
      ({ data }) => setAuxLibroMayorNumber(data.numero)
    )
  }

  useCuentaContableCuentaAll()
  useCuentaContableEmpresaAll()

  useEffect(() => {
    fetchCuentaContableEmpresa(params.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOpenDocumentoModal = async (id: string) => {
    const facturaById = await getFacturasByIdSimple(id)
    setModalDocumentoDetail({
      show: true,
      document: facturaById,
    })
  }

  const fetchLibroMayor = useCallback(
    () => (queryParams: $TSMigrationFixMe) => {
      LineaAsientosContableCall?.cancel()
      return getLineaAsientosContable({
        ...queryParams,
        CuentaContableId: params.id,
        SortingCriteria: queryParams.SortingCriteria,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const {
    items,
    isLoading,
    totalCount,
    setServiceQuery,
    fetchTableData,
    serviceQuery,
  } = useFilterMenu({
    serviceCall: fetchLibroMayor,
  })

  useEffect(() => {
    scrollToTop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  useEffect(() => {
    serviceQuery &&
      getImportesTotalesAsientoContable({
        ...serviceQuery,
        CuentaContableId: params.id,
      }).then((res) => {
        setTotals({
          // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'null'.
          debeTotal: res.debeTotal,
          // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'null'.
          haberTotal: res.haberTotal,
          // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'null'.
          saldoAcumulado: res.saldoAcumulado,
          // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'null'.
          saldoPeriodo: res.saldoPeriodo,
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  useEffect(() => {
    if (!showModalAsiento) {
      // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param...
      setAsientoSelectedEdit(null)
    }
  }, [showModalAsiento])

  const exportToExcel = async () => {
    const { data } = await getAsientoContableExportarExcel({
      // @ts-expect-error TS(2345): Argument of type '{ FechaLowerLimit: string; Fecha...
      FechaLowerLimit: date.min ? backendDateFormat(date.min) : "",
      FechaUpperLimit: date.max ? backendDateFormat(date.max) : "",
      CuentaContableId: params.id,
    })
    handleDirectDownloadExcel(data, `${t("exportarXlxs.libroMayor")}.xlsx`)
    setDate(DEFAULT_DATE)
  }

  const handleOpenModalAsientoContable = (
    data: LineasAsientosContablesByFilterDtoIProps
  ) => {
    if (data.asientoContable) {
      setShowModalAsiento(true)
      setAsientoSelectedEdit(data.asientoContable.id)
    }
  }
  const onRowClick = async ({
    data,
    originalEvent: { target },
  }: DataTableRowClickEventParams) => {
    const cell = target as HTMLTableCellElement
    if (!data.id || !cell?.cellIndex) return

    handleOpenModalAsientoContable(data)
  }

  return (
    <section className='tw-flex tw-flex-col tw-order-2 tw-z-10 tw-relative tw-w-full tw-max-h-screen tw-h-full tw-max-w-screen tw-bg-gray tw-overflow-x-hidden tw-overflow-y-hidden custom-scrollbar'>
      <div className='tw-flex-none tw-bg-white tw-shadow-md'>
        <FilterMenu
          serviceQuery={setServiceQuery}
          resetPagination={handleResetPagination}
          createAsiento={() => setShowModalAsiento(true)}
          exportToExcel={() => setShowExportDatesModal(true)}
          title={auxLibroMayorNumber}
          sortingCriteria={sortingCriteria}
          maxResultCount={pagination.itemsPerPage}
          skipCount={pagination.skipCount}
          selectedAsientoTipo={selectedAsientoTipo}
          setSelectedAsientoTipo={setSelectedAsientoTipo}
          keyword={keyword}
          setKeyword={setKeyword}
          amount={amount}
          setAmount={setAmount}
        />
      </div>

      <div className='tw-flex tw-flex-col tw-h-[calc(100%-140px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={LIBRO_MAYOR_COLUMNS(t, handleOpenDocumentoModal)}
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            lazy
            dataKey='id'
            scrollable
            size='small'
            onRowClick={onRowClick}
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
          <section className='tw-flex tw-justify-end tw-px-5 tw-py-4 tw-text-base tw-text-text tw-bg-white'>
            <span className='px-2'>
              {t("mayor.totalDebitLabel")}{" "}
              <b>
                {totals?.debeTotal
                  ? Intl.NumberFormat("de-DE", {
                      style: "currency",
                      currency: "EUR",
                    }).format(totals?.debeTotal)
                  : "0,00 €"}
              </b>
            </span>
            <span className='px-2'>
              {t("mayor.totalCreditLabel")}{" "}
              <b>
                {totals?.haberTotal
                  ? Intl.NumberFormat("de-DE", {
                      style: "currency",
                      currency: "EUR",
                    }).format(totals?.haberTotal)
                  : "0,00"}
              </b>
            </span>
            <span className='px-2'>
              {t("mayor.periodBalanceLabel")}{" "}
              <b>
                {totals?.saldoPeriodo
                  ? Intl.NumberFormat("de-DE", {
                      style: "currency",
                      currency: "EUR",
                    }).format(totals?.saldoPeriodo)
                  : "0,00"}
              </b>
            </span>
            <span className='px-2'>
              {t("mayor.acumulatedBalanceLabel")}{" "}
              <b>
                {totals?.saldoAcumulado
                  ? Intl.NumberFormat("de-DE", {
                      style: "currency",
                      currency: "EUR",
                    }).format(totals?.saldoAcumulado)
                  : "0,00"}
              </b>
            </span>
          </section>
          <PaginationERP
            className={PAGINATION_STYLES}
            totalRecords={totalCount}
            first={pagination.skipCount}
            rows={pagination.itemsPerPage}
            resetScroll={() => {
              dtRef.current && dtRef.current?.resetScroll()
            }}
            setPagination={setPagination}
            optionDropdown={[10, 20, 50, 100]}
            texts={{
              textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
              textCurrentPageReport: `${t("pagination.from")}`,
            }}
          />
        </div>
      </div>

      <ModalAsientoContable
        idToEdit={asientoSelectedEdit}
        show={showModalAsiento}
        setShow={setShowModalAsiento}
        updateTable={fetchTableData}
      />
      <ModalVerDocumento
        showModal={modalDocumentoDetail.show}
        onHide={() =>
          setModalDocumentoDetail({
            show: false,
            document: null,
          })
        }
        docType={TIPO_DOCUMENTO_ENUM.FACTURA}
        documentoID={modalDocumentoDetail.document?.id ?? null}
        typeDocumentTable='facturas'
      />
      <ModalFechas
        isOpen={showExportDatesModal}
        date={date}
        setDate={setDate}
        onCancel={() => setShowExportDatesModal(false)}
        onConfirm={exportToExcel}
        text={`${t("diario.exportModal.dateRange")}`}
      />
    </section>
  )
}

export default LibroMayor
